import React, { useRef, useEffect } from "react";
import CustomInput from "../../../components/controls/input/customInput/CustomInput";
import { calculateDurationNoWeekends, calculateEndDateNoWeekends, calculateStartDateNoWeekends, getTodayBusinessDateNoWeekends } from "../utils/date";

export const TaskInputWithDates = ({
  scope,
  index,
  taskName,
  taskProgress,
  handleTaskNameChange,
  handler,
  newTaskDates,
  setNewTaskDates,
  isEditing = false,
  handleTaskProgressChange,
  columnWidths,
  sidebarWidth,
  isNew = false,
  autoFocusTask = false,
  onCancel,
  isHideTask = false
}) => {

  const containerRef = useRef(null);
  const taskNameInputRef = useRef(null);

  // Get current dates and duration
  const currentStartDate = isEditing ? newTaskDates?.startDate : newTaskDates[scope.id]?.startDate || "";
  const currentEndDate = isEditing ? newTaskDates?.endDate : newTaskDates[scope.id]?.endDate || "";
  const currentDuration = currentStartDate && currentEndDate
    ? calculateDurationNoWeekends(currentStartDate, currentEndDate)
    : 0;

  const handleDurationChange = (value) => {
    if(!value || value === '')
      return;

    let duration = parseInt(value) || 0;

    const currentProgress = isEditing ? taskProgress : taskProgress[scope.id];
    const isStartDateDisabled = Number(currentProgress) > 0;

    // Allow clearing duration
    if (duration <= 0) {
      if (isEditing) {
        setNewTaskDates((prevState) => ({
          ...prevState,
          startDate: '',
          endDate: '',
        }));
      } else {
        setNewTaskDates((prevState) => ({
          ...prevState,
          [scope.id]: {
            startDate: '',
            endDate: '',
          },
        }));
      }
      return;
    }

    // If both dates exist, prioritize start date and calculate end date
    if (currentStartDate && currentEndDate) {
      const newEndDate = calculateEndDateNoWeekends(currentStartDate, duration);

      if (isEditing) {
        setNewTaskDates((prevState) => ({
          ...prevState,
          endDate: newEndDate,
        }));
      } else {
        setNewTaskDates((prevState) => ({
          ...prevState,
          [scope.id]: {
            ...prevState[scope.id],
            endDate: newEndDate,
          },
        }));
      }
    }
    // If only end date exists, calculate start date (only if start date is not disabled)
    else if (currentEndDate && !isStartDateDisabled) {
      const newStartDate = calculateStartDateNoWeekends(currentEndDate, duration);

      if (isEditing) {
        setNewTaskDates((prevState) => ({
          ...prevState,
          startDate: newStartDate,
        }));
      } else {
        setNewTaskDates((prevState) => ({
          ...prevState,
          [scope.id]: {
            ...prevState[scope.id],
            startDate: newStartDate,
          },
        }));
      }
    }
    // If only start date exists, calculate end date
    else if (currentStartDate) {
      const newEndDate = calculateEndDateNoWeekends(currentStartDate, duration);

      if (isEditing) {
        setNewTaskDates((prevState) => ({
          ...prevState,
          endDate: newEndDate,
        }));
      } else {
        setNewTaskDates((prevState) => ({
          ...prevState,
          [scope.id]: {
            ...prevState[scope.id],
            endDate: newEndDate,
          },
        }));
      }
    }
    // If no dates exist, use today as start date (only if start date is not disabled)
    else if (!isStartDateDisabled) {
      const todayDate = getTodayBusinessDateNoWeekends();
      const newEndDate = calculateEndDateNoWeekends(todayDate, duration);

      if (isEditing) {
        setNewTaskDates((prevState) => ({
          ...prevState,
          startDate: todayDate,
          endDate: newEndDate,
        }));
      } else {
        setNewTaskDates((prevState) => ({
          ...prevState,
          [scope.id]: {
            ...prevState[scope.id],
            startDate: todayDate,
            endDate: newEndDate,
          },
        }));
      }
    }
  };

  const handleStartDateChange = (value) => {
    // Update start date
    if (isEditing) {
      setNewTaskDates((prevState) => ({
        ...prevState,
        startDate: value,
      }));
    } else {
      setNewTaskDates((prevState) => ({
        ...prevState,
        [scope.id]: {
          ...prevState[scope.id],
          startDate: value,
        },
      }));
    }

    // If end date exists, it stays the same (duration auto-updates)
    // If end date doesn't exist and we have a value, do nothing (user can set it)
    // If clearing start date, also clear end date
    if (!value || value === '') {
      if (isEditing) {
        setNewTaskDates((prevState) => ({
          ...prevState,
          endDate: '',
        }));
      } else {
        setNewTaskDates((prevState) => ({
          ...prevState,
          [scope.id]: {
            ...prevState[scope.id],
            endDate: '',
          },
        }));
      }
    }
  };

  const handleEndDateChange = (value) => {
    const currentProgress = isEditing ? taskProgress : taskProgress[scope.id];
    const isStartDateDisabled = Number(currentProgress) > 0;

    // Update end date
    if (isEditing) {
      setNewTaskDates((prevState) => ({
        ...prevState,
        endDate: value,
      }));
    } else {
      setNewTaskDates((prevState) => ({
        ...prevState,
        [scope.id]: {
          ...prevState[scope.id],
          endDate: value,
        },
      }));
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!containerRef.current?.contains(e.target)) return;

      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        handler(scope.id);
      }

      else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onCancel(scope.id);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [scope.id, index, handler]);

  useEffect(() => {
    if (autoFocusTask && taskNameInputRef.current) {
      taskNameInputRef.current.focus();
    }
  }, [autoFocusTask]);

  return (
    <div
      ref={containerRef}
      className={`h-[20px] flex flex-row items-center`}
      style={{ width: `${sidebarWidth}px` }}
    >
      {/* Task Name */}
      <input
        ref={taskNameInputRef}
        type="text"
        className={`${
          index % 2 === 0 ? "" : "bg-[#f5f5f5]"
        } ${isNew ? 'pl-[3rem]' : 'pl-8'} pl-8 h-[20px] w-full outline-none text-s font-medium focus:placeholder-transparent`}
        placeholder={"new task"}
        value={isEditing ? taskName : taskName[scope.id]}
        onChange={(event) => handleTaskNameChange(scope.id, event.target.value)}
        style={{
          width: columnWidths.scopes -22,
        }}
      />
      {/* Task Progress */}
      <input
        type="number"
        min={0}
        max={100}
        className={`${
          index % 2 === 0 ? "" : "bg-[#f5f5f5]"
        } h-[20px] w-full outline-none text-s text-center pl-1`}
        placeholder={"0%"}
        maxLength={2}
        value={isEditing ? taskProgress : taskProgress[scope.id]}
        onChange={(event) =>
          handleTaskProgressChange(scope.id, event.target.value)
        }
        style={{
          width: columnWidths.completed,
        }}
      />
      {/* Duration */}
      <input
        type="number"
        min={0}
        className={`${
          index % 2 === 0 ? "" : "bg-[#f5f5f5]"
        } h-[20px] w-full outline-none text-s text-center pl-1`}
        placeholder={"0d"}
        value={currentDuration > 0 ? currentDuration : ''}
        onChange={(event) => handleDurationChange(event.target.value)}
        style={{
          width: columnWidths.duration,
        }}
      />
      {/* Start Date */}
      <div
        className="h-[20px] flex items-center justify-center mb-1"
        style={{
          width: columnWidths.startDate,
        }}
      >
        <CustomInput
          type="date"
          width="80px"
          height="18px"
          DateStyleClass={`date-input DatePickerInput text-s font-medium ${Number(taskProgress) > 0 ?'cursor-not-allowed' :''}`}
          DateStyles={{
            fontSize: "10px",
            backgroundColor: "transparent !important",
          }}
          maxDate={currentEndDate}
          handleChange={handleStartDateChange}
          value={currentStartDate}
          isDisabled={Number(taskProgress) > 0}
        />
      </div>
      {/* End Date */}
      <div
        className="h-[20px] flex items-center justify-center mb-1"
        style={{
          width: columnWidths.endDate,
        }}
      >
        <CustomInput
          type="date"
          width="80px"
          height="18px"
          DateStyleClass="date-input DatePickerInput"
          DateStyles={{
            fontSize: "10px",
            backgroundColor: "transparent !important",
          }}
          minDate={currentStartDate}
          handleChange={handleEndDateChange}
          value={currentEndDate}
        />
      </div>
    </div>
  );
};

